import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Env, getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import { useBusinessAnalytics } from '@/pages/B2B/business-analytics/api/useBusinessAnalytics';
import { TIMEFRAMES, Timeframe, getTimeframeRange, topN } from './timeframes';
import { METRIC_CATALOG } from './metricCatalog';
import LeaderboardCard from './LeaderboardCard';

function StatSheetsPage(): React.ReactElement {
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [timeframe, setTimeframe] = useState<Timeframe>('this_week');
  const apiUrl = getAdminApiUrl(selectedEnv);

  const { since, until } = useMemo(() => getTimeframeRange(timeframe), [timeframe]);
  const { data, loading, error, refetch } = useBusinessAnalytics(apiUrl, { since, until });

  const timeframeLabel = TIMEFRAMES.find((t) => t.key === timeframe)?.label ?? '';
  const businesses = data.businesses;
  const hasData = !loading && !error && businesses.length > 0;

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/socials" className="text-sm text-gray-500 hover:text-gray-300 transition-colors no-underline">Socials</Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">Stat Sheets</span>
        </div>

        {/* Header + env switcher */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white font-display tracking-tight">Stat Sheets</h1>
            <p className="text-xs text-gray-400 mt-0.5">Top businesses by engagement — export any board for social posts.</p>
          </div>
          <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
            {(['dev', 'staging', 'prod'] as Env[]).map((env) => {
              const isConfigured = Boolean(getAdminApiUrl(env));
              const isActive = selectedEnv === env;
              return (
                <button
                  key={env}
                  onClick={() => isConfigured && setSelectedEnv(env)}
                  disabled={!isConfigured}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                    isActive ? ENV_COLORS[env].pill
                      : isConfigured ? 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
                      : 'border-transparent text-gray-700 cursor-not-allowed'
                  }`}
                >
                  {env.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeframe selector. Calendar periods on purpose: a board posted to
            socials is titled "This Week", not "last 7 days". */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl w-fit mb-2">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.key}
              onClick={() => setTimeframe(tf.key)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                timeframe === tf.key
                  ? 'border-violet-500/40 text-violet-300 bg-violet-900/30'
                  : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <p className="text-xs text-gray-500 mb-6">
          {timeframe === 'all_time'
            ? 'Everything on record.'
            : `Calendar ${timeframeLabel.toLowerCase()}, not a trailing window: ${new Date(since).toLocaleDateString()} to now${
                timeframe === 'this_week' ? ' (weeks start Monday)' : ''
              }.`}
        </p>

        {/* Error */}
        {error && (
          <div className="mb-8 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button onClick={refetch} className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer">Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty */}
        {!loading && !error && businesses.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
            <p className="text-gray-400 text-sm">No data for this timeframe yet.</p>
          </div>
        )}

        {/* Boards */}
        {hasData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {METRIC_CATALOG.map((metric) => (
              <LeaderboardCard
                key={metric.key}
                metric={metric}
                rows={topN(businesses, metric.accessor, (r) => r.business_name, 5)}
                timeframeLabel={timeframeLabel}
                timeframeKey={timeframe}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default StatSheetsPage;
